import React from 'react'
import { Button, Chip, IconButton, MenuItem, Select, SelectChangeEvent, Tooltip, useTheme } from '@mui/material'
import { CloseOutlined, DeleteOutline } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { ITableColumnConfig } from 'src/types/report-designer'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { AppTextField } from '../../AppTextField'
import {
  ColumnCard,
  ColumnCardHeader,
  ColumnHeaderLeft,
  ColumnNameText,
  ColumnHeaderActions,
  ColumnEditBody,
  ColumnFieldRow,
  RequiredAsterisk,
  ColumnFieldLabel,
  PlaceholderText,
} from './TableComponentProperties.style'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'

export interface IAddNewColumnCardProps {
  columns: ITableColumnConfig[]
  draftColumn: ITableColumnConfig
  availableFieldsForDraft: IFormSchemaColumn[]
  schemaColumns?: IFormSchemaColumn[]
  hideWidthConfig?: boolean
  onCancelEdit: () => void
  onDeleteColumn: (col: ITableColumnConfig) => void
  onDraftFieldSelect: (fieldId: string) => void
  onDraftWidthChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSaveColumn: () => void
}

export const AddNewColumnCard: React.FC<IAddNewColumnCardProps> = ({
  columns,
  draftColumn,
  availableFieldsForDraft,
  schemaColumns,
  hideWidthConfig,
  onCancelEdit,
  onDeleteColumn,
  onDraftFieldSelect,
  onDraftWidthChange,
  onSaveColumn,
}) => {
  const { t: translate } = useTranslation(['dynamic-report', 'common'])
  const theme = useTheme()

  return (
    <ColumnCard isDragOver={false} isEditing={true}>
      <ColumnCardHeader>
        <ColumnHeaderLeft>
          <Chip
            label={`#${columns.length + 1}`}
            size="small"
            sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }}
          />
          <ColumnNameText isPlaceholder={true} color={getThemeColor(theme, MUIThemeColors.errorDark)}>
            {translate('select-field-placeholder', { ns: 'dynamic-report' })}
          </ColumnNameText>
        </ColumnHeaderLeft>
        <ColumnHeaderActions>
          <Tooltip title={`${translate('cancel', { ns: 'common' })}`}>
            <IconButton size="small" onClick={onCancelEdit}>
              <CloseOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={`${translate('delete-column', { ns: 'dynamic-report' })}`}>
            <IconButton size="small" onClick={() => onDeleteColumn(draftColumn)}>
              <DeleteOutline fontSize="small" sx={{ color: 'error.main' }} />
            </IconButton>
          </Tooltip>
        </ColumnHeaderActions>
      </ColumnCardHeader>

      <ColumnEditBody>
        <ColumnFieldRow>
          <ColumnFieldLabel>
            {translate('column-name', { ns: 'dynamic-report' })}
            <RequiredAsterisk>*</RequiredAsterisk>
          </ColumnFieldLabel>
          <Select
            size="small"
            fullWidth
            value={draftColumn.formSchemaFieldId ?? ''}
            onChange={(e: SelectChangeEvent<string | ''>) => {
              if (e.target.value !== '') onDraftFieldSelect(e.target.value)
            }}
            displayEmpty
            renderValue={selected => {
              if (selected === '') {
                return (
                  <PlaceholderText>{translate('select-field-placeholder', { ns: 'dynamic-report' })}</PlaceholderText>
                )
              }
              const field = schemaColumns?.find(f => f.identifier === selected)
              return field?.name ?? String(selected)
            }}
          >
            {availableFieldsForDraft.map(field => (
              <MenuItem key={field.identifier} value={field.identifier}>
                {field.name}
              </MenuItem>
            ))}
          </Select>
        </ColumnFieldRow>

        {!hideWidthConfig && (
          <AppTextField
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 20, max: 500 }}
            label={translate('column-width', { ns: 'dynamic-report' })}
            value={draftColumn.width !== null ? String(draftColumn.width) : ''}
            placeholder={translate('auto', { ns: 'dynamic-report' })}
            onChange={onDraftWidthChange}
            error={
              draftColumn.width !== null &&
              draftColumn.width !== undefined &&
              (draftColumn.width < 20 || draftColumn.width > 500)
            }
            helperText={
              draftColumn.width !== null &&
              draftColumn.width !== undefined &&
              (draftColumn.width < 20 || draftColumn.width > 500)
                ? translate('column-width-range-error', { ns: 'dynamic-report' })
                : undefined
            }
          />
        )}

        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={onSaveColumn}
          disabled={
            !draftColumn.formSchemaFieldId ||
            (draftColumn.width !== null &&
              draftColumn.width !== undefined &&
              (draftColumn.width < 20 || draftColumn.width > 500))
          }
          sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600 }}
        >
          {translate('save-column', { ns: 'dynamic-report' })}
        </Button>
      </ColumnEditBody>
    </ColumnCard>
  )
}
